import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { StoreApi, useStore } from "zustand";
import AppLoading from "components/AppLoading/AppLoading";
import { CallOutError } from "components/CallOutError/CallOutError";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { useUploadMultiPartFile } from "hooks/useUploadMultipartFile/useUploadMultiPartFile";
import { uuid } from "utils/uuid";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import {
  createToolEditorStore,
  CreateToolEditorStoreParams,
  ToolEditorPageKey,
  ToolEditorStoreState,
} from "./store/ToolEditorProvider.store";
import {
  TOOL_EDITOR_PAGE_PARAM,
  TOOL_EDITOR_PAGE_PARAM_VALUES,
} from "hooks/useIdeasSearchParams";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  useDataTableContext,
  useRefetchDataTableStore,
} from "pages/gdt/store/DataTableProvider";
import { DjangoClient } from "api/client/DjangoClient";

const ToolEditorContext = createContext<
  StoreApi<ToolEditorStoreState> | undefined
>(undefined);

export const useToolEditorContext = <T,>(
  selector: (state: ToolEditorStoreState) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => {
  const store = useContext(ToolEditorContext);

  if (store === undefined) {
    throw new ContextOutOfBoundsError("ToolEditorContext");
  }
  return useStore(store, selector, equalityFn);
};

export interface ToolEditorProviderProps {
  toolVersionId: number;
  children: ReactNode;
}

export const ToolEditorProvider = ({
  toolVersionId,
  children,
}: ToolEditorProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const [store, setStore] = useState<StoreApi<ToolEditorStoreState>>();

  const { uploadMultiPartFile } = useUploadMultiPartFile("CODE");
  const currentTenantId = useTenantContext((s) => s.currentTenant.id);

  const refetchDataTableStore = useRefetchDataTableStore();
  const setSelectedTableId = useDataTableContext((s) => s.setSelectedTableId);

  const navigate = useNavigate();
  /**
   * Handler to upload the file to the backend
   */
  const uploadFile: CreateToolEditorStoreParams["uploadFile"] = useCallback(
    async (file, bundleMetadata, onUploadProgress, checksum) => {
      const codeFile = await DjangoClient.code_file.create.queryFn({
        id: uuid(),
        tenantId: currentTenantId,
        size: file.size,
        name: file.name,
        bundle_metadata: bundleMetadata,
        tool_version: toolVersionId,
        checksum: checksum,
      });
      await uploadMultiPartFile({
        file: {
          id: codeFile.id,
          blob: file,
          partSize: codeFile.part_size,
          tenantId: codeFile.tenant,
          type: "CODE",
          toolVersionId,
          bundleMetadata,
        },
        onUploadProgress,
      });
    },
    [currentTenantId, toolVersionId, uploadMultiPartFile],
  );

  const setTestTableId = useCallback(
    (testTableId: string) => setSelectedTableId(testTableId, false),
    [setSelectedTableId],
  );

  const routeToPage = useCallback(
    (page: ToolEditorPageKey) => {
      let params: URLSearchParams;

      switch (page) {
        case "CODE":
          params = new URLSearchParams([
            [
              TOOL_EDITOR_PAGE_PARAM,
              "code" satisfies TOOL_EDITOR_PAGE_PARAM_VALUES,
            ],
          ]);
          break;
        case "SPEC":
          params = new URLSearchParams([
            [
              TOOL_EDITOR_PAGE_PARAM,
              "config" satisfies TOOL_EDITOR_PAGE_PARAM_VALUES,
            ],
          ]);
          break;

        case "TEST":
          params = new URLSearchParams([
            [
              TOOL_EDITOR_PAGE_PARAM,
              "test" satisfies TOOL_EDITOR_PAGE_PARAM_VALUES,
            ],
          ]);
          break;

        case "PUBLISH":
          params = new URLSearchParams([
            [
              TOOL_EDITOR_PAGE_PARAM,
              "publish" satisfies TOOL_EDITOR_PAGE_PARAM_VALUES,
            ],
          ]);
          break;
      }

      navigate(`${window.location.pathname}?${params.toString()}`, {
        replace: true,
      });
    },
    [navigate],
  );

  useEffect(() => {
    if (store === undefined) {
      createToolEditorStore({
        toolVersionId,
        refetchDataTableStore,
        setTestTableId,
        uploadFile,
        routeToPage,
      })
        .then((store) => {
          setStore(store);
        })
        .catch(() => {
          setError(new Error("Failed to retrieve tool version."));
        })
        .finally(() => setLoading(false));
    }
  }, [
    toolVersionId,
    uploadFile,
    routeToPage,
    store,
    refetchDataTableStore,
    setTestTableId,
  ]);

  useEffect(() => {
    /**
     * Subscribe to terminal form error in store to capture and render error callout
     */
    const unsubscribe = store?.subscribe((state) => {
      if (state.formError instanceof Error) {
        setError(state.formError);
      }
    });

    return () => unsubscribe?.();
  }, [store]);

  /**
   * Warn users before leaving while uploading
   */

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const unsubscribe = store?.subscribe((state, prevState) => {
      /**
       * When upload starts, add listener
       */
      if (
        state.codeUploadStatus === "UPLOADING" &&
        prevState.codeUploadStatus !== "UPLOADING"
      ) {
        window.addEventListener("beforeunload", beforeUnload);
      }

      /** When upload finishes, add remove listener */
      if (
        state.codeUploadStatus !== "UPLOADING" &&
        prevState.codeUploadStatus === "UPLOADING"
      ) {
        window.removeEventListener("beforeunload", beforeUnload);
      }
    });

    /**
     * Unsubscribe and remove listener on unmount
     */
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      unsubscribe?.();
    };
  }, [store]);

  /**
   * Warn users before leaving with unsynced changes
   */

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const unsubscribe = store?.subscribe((state, prevState) => {
      /**
       * Add listener when unsynced changes are made
       */
      if (
        state.draftSpecSaveStatus !== "SAVED" &&
        prevState.draftSpecSaveStatus === "SAVED"
      ) {
        window.addEventListener("beforeunload", beforeUnload);
      }

      /**
       * Remove listener when changes are synced
       */

      if (
        state.draftSpecSaveStatus === "SAVED" &&
        prevState.draftSpecSaveStatus !== "SAVED"
      ) {
        window.removeEventListener("beforeunload", beforeUnload);
      }
    });

    /**
     * Unsubscribe and remove listener on unmount
     */
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      unsubscribe?.();
    };
  }, [store]);

  /**
   * Keep store up to date with dependencies
   */
  useEffect(() => {
    store?.setState((prev) => ({ ...prev, refetchDataTableStore }));
  }, [refetchDataTableStore, store]);

  if (loading) {
    return <AppLoading />;
  }

  if (error !== undefined || store === undefined) {
    return <CallOutError message={error?.message ?? "Unknown Error"} />;
  }

  return (
    <ToolEditorContext.Provider value={store}>
      {children}
    </ToolEditorContext.Provider>
  );
};
